import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody, CardHeader, Table, Badge } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import "moment/locale/es";
import { formatCurrency } from "../../utils/formatCurrency";
import ChartTypeToggle from "./ChartTypeToggle";
import ExpandableChart from "./ExpandableChart";
import PaymentBreakdown from "./PaymentBreakdown";
import { CategorySummary, useCategories } from "./CategoryBreakdown";
import { SourceSelector, applySourceFilter } from "./sourceFilter";
import { sinDiaEnCurso } from "./partialDay";
import { TOOLBAR_DESCARGA, LEYENDA } from "./graficos";
import { BotonesExportar } from "./exportar";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// Misma paleta que el resto de la pantalla: el color se pide por concepto.
import { MODALIDAD, ORIGEN, POLARIDAD } from "../Stats2/theme";

const fmt = (v) => formatCurrency(v ?? 0, true);

const pct = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / prev) * 100;
};

const GrowthBadge = ({ value }) => {
  if (value == null || !isFinite(value)) return <span className="text-muted">-</span>;
  const up = value >= 0;
  return (
    <Badge color={up ? "success" : "danger"} className="px-2" style={{ fontSize: 11 }}>
      {up ? "+" : ""}{value.toFixed(1)}%
    </Badge>
  );
};

// ── Monthly income/expense chart ─────────────────────────────
const MonthlyBarChart = ({ monthlyDocs, source = "all", heightOverride }) => {
  const [chartType, setChartType] = useState("bar");
  const sorted = useMemo(
    () => [...monthlyDocs].sort((a, b) => a.month.localeCompare(b.month)),
    [monthlyDocs]
  );

  const categories = useMemo(
    () => sorted.map((d) => {
      const m = moment(d.month, "YYYY-MM");
      return m.isValid() ? m.format("MMM YY") : d.month;
    }),
    [sorted]
  );

  // Egresos no se atribuyen a una fuente; sólo aplican cuando se ven todos
  // los ingresos. Para "tickets" y "mensuales" mostramos sólo ingresos.
  const showExpenses = source === "all";

  const options = useMemo(
    () => ({
      chart: { type: chartType, height: heightOverride || 320, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Lo que entra y lo que sale: POLARIDAD. Lo que las separa de verdad no
      // es el color sino el signo, pero el color acompaña.
      colors: showExpenses ? [POLARIDAD.entra.color, POLARIDAD.sale.color] : [POLARIDAD.entra.color],
      plotOptions: chartType === "bar" ? { bar: { columnWidth: "50%", borderRadius: 4 } } : {},
      stroke: { curve: "smooth", width: chartType === "bar" ? 0 : 2.5 },
      fill: chartType === "area"
        ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } }
        : { opacity: 1 },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { style: { fontSize: "12px", textTransform: "capitalize" } } },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
      // Con egresos son dos series y hay que poder distinguirlas. Al filtrar
      // por origen queda una sola y la leyenda se apaga sola: es el
      // `showForSingleSeries: false` de la constante compartida.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [categories, chartType, showExpenses]
  );

  const series = useMemo(
    () => {
      const incomeLabel = source === "tickets" ? "Ingresos por tickets"
        : source === "monthly" ? "Ingresos de abonados"
        : "Ingresos";
      const arr = [{ name: incomeLabel, data: sorted.map((d) => d.income || 0) }];
      if (showExpenses) {
        arr.push({ name: "Salidas de caja", data: sorted.map((d) => d.expense || 0) });
      }
      return arr;
    },
    [sorted, source, showExpenses]
  );

  const titleSuffix = source === "tickets" ? " — solo Tickets"
    : source === "monthly" ? " — solo Abonados"
    : "";

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>bar_chart</span>
          Cuánto dejó cada mes
          {titleSuffix && <span className="ms-1" style={TITULO_ACLARACION}>({titleSuffix.replace(" — ", "")})</span>}
        </h6>
        <ChartTypeToggle value={chartType} onChange={setChartType} />
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart key={chartType} options={options} series={series} type={chartType} height={heightOverride || 320} />
      </CardBody>
    </Card>
  );
};

// ── Monthly trends (daily avg income + tickets/day) ──────────
const MonthlyTrendsChart = ({ monthlyDocs, heightOverride }) => {
  const sorted = useMemo(
    () => [...monthlyDocs].sort((a, b) => a.month.localeCompare(b.month)),
    [monthlyDocs]
  );

  const categories = useMemo(
    () => sorted.map((d) => moment(d.month, "YYYY-MM").format("MMM YY")),
    [sorted]
  );

  const options = useMemo(
    () => ({
      chart: { type: "line", height: heightOverride || 260, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // Dos ejes distintos: plata que entró y cantidad de tickets. Uno es
      // POLARIDAD, el otro es la identidad de Tickets.
      colors: [POLARIDAD.entra.color, ORIGEN.tickets.color],
      stroke: { curve: "smooth", width: 2.5 },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { style: { fontSize: "11px", textTransform: "capitalize" } } },
      yaxis: [
        {
          title: { text: "Ingreso diario", style: { fontSize: "11px" } },
          labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } },
        },
        {
          opposite: true,
          title: { text: "Tickets por día", style: { fontSize: "11px" } },
          labels: { style: { fontSize: "10px" } },
        },
      ],
      tooltip: {
        shared: true,
        y: [
          { formatter: (v) => formatCurrency(v, true) },
          { formatter: (v) => (v != null ? v.toFixed(0) : "0") },
        ],
      },
      // Dos líneas con escalas distintas —plata a la izquierda, autos a la
      // derecha—: sin leyenda no se sabe cuál línea se lee contra cuál eje.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [categories]
  );

  const series = useMemo(
    () => [
      { name: "Ingreso diario", data: sorted.map((d) => Math.round(d.averages?.daily_income || 0)) },
      { name: "Tickets por día", data: sorted.map((d) => Math.round(d.averages?.daily_tickets_in || 0)) },
    ],
    [sorted]
  );

  if (sorted.length < 2) return null;

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>trending_up</span>
          Cómo viene tu día promedio, mes a mes
          <span className="ms-1" style={TITULO_ACLARACION}>
            (lo que deja un día cualquiera y cuántos autos entran)
          </span>
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={series} type="line" height={heightOverride || 260} />
      </CardBody>
    </Card>
  );
};

// ── Revenue composition by month (stacked bar) ──────────────
// `source` filtra qué series se muestran. En "monthly" sólo queda 1 serie
// (es redundante con MonthlyBarChart) — el caller decide si renderizar.
const RevenueCompositionChart = ({ monthlyDocs, source = "all", heightOverride }) => {
  const sorted = useMemo(
    () => [...monthlyDocs].sort((a, b) => a.month.localeCompare(b.month)),
    [monthlyDocs]
  );

  const categories = useMemo(
    () => sorted.map((d) => moment(d.month, "YYYY-MM").format("MMM YY")),
    [sorted]
  );

  // Cada serie viaja con su color, porque abajo se filtran: por origen y por
  // monto en cero. Con una lista de colores aparte, el que se corría un puesto
  // se llevaba el color del de arriba.
  //
  // Las cuatro primeras son MODALIDADES DE COBRO, no orígenes distintos
  // —"Estadía" a secas parecía contestar cuánto se queda la gente—, así que van
  // en la rampa del azul de Tickets: misma familia, ordenada por duración.
  //
  // Con las siete series prendidas, "Ingresos manuales" (violeta) y "Tickets
  // cobrados por turno" (azul oscuro) quedan cerca para un ojo con
  // deuteranopía. No hay paleta que aguante siete orígenes: la salida real es
  // agrupar, que es cambiar qué muestra el gráfico y no de dónde saca el color.
  // Igual son dos tonos distintos donde antes había dos series pintadas
  // exactamente del mismo color, porque los colores eran cinco.
  const series = useMemo(() => {
    const all = [
      { name: "Tickets cobrados por hora", key: "ticket", color: MODALIDAD.hour.color, data: sorted.map((d) => d.tickets?.hour?.amount || 0) },
      { name: "Tickets cobrados por estadía", key: "ticket", color: MODALIDAD.stay.color, data: sorted.map((d) => d.tickets?.stay?.amount || 0) },
      { name: "Tickets cobrados por turno", key: "ticket", color: MODALIDAD.tn.color, data: sorted.map((d) => d.tickets?.tn?.amount || 0) },
      { name: "Tickets con tarifa plana", key: "ticket", color: MODALIDAD.plana.color, data: sorted.map((d) => d.tickets?.plana?.amount || 0) },
      { name: "Abonados", key: "monthly", color: ORIGEN.abonados.color, data: sorted.map((d) => d.monthly?.amount || 0) },
      { name: "Acuerdos", key: "ticket", color: ORIGEN.convenios.color, data: sorted.map((d) => d.deals?.amount || 0) },
      // `manual` no es ni ticket ni mensual: se muestra sólo con "Todos", igual
      // que en el filtro de origen compartido. Sin esta serie la barra apilada
      // no llegaba nunca a la altura del total del mes.
      { name: "Ingresos manuales", key: "manual", color: ORIGEN.otros.color, data: sorted.map((d) => d.manual?.amount || 0) },
    ];
    let filtered = all;
    if (source === "tickets") filtered = all.filter((s) => s.key === "ticket");
    else if (source === "monthly") filtered = all.filter((s) => s.key === "monthly");
    return filtered.filter((s) => s.data.some((v) => v > 0));
  }, [sorted, source]);

  const options = useMemo(
    () => ({
      chart: { type: "bar", stacked: true, height: heightOverride || 280, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      // El color sale de la serie que quedó después de los filtros, no de una
      // lista fija en paralelo: con la lista, al ocultarse una serie las de
      // abajo se corrían un puesto y cambiaban de color.
      colors: series.map((s) => s.color),
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 2 } },
      dataLabels: { enabled: false },
      xaxis: { categories, labels: { style: { fontSize: "11px", textTransform: "capitalize" } } },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "11px" } } },
      tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
      // Cada barra apila hasta siete orígenes: la leyenda es lo único que dice
      // qué franja es cuál.
      legend: LEYENDA,
      grid: { borderColor: "#f1f1f1" },
    }),
    [categories, series]
  );

  if (!series.length) return null;

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>stacked_bar_chart</span>
          De dónde salió la plata de cada mes
        </h6>
      </CardHeader>
      <CardBody className="pt-1">
        <ReactApexChart options={options} series={series} type="bar" height={heightOverride || 280} />
      </CardBody>
    </Card>
  );
};

// ── Current month projection ─────────────────────────────────
// dailyDocs y monthlyDocs deben venir YA filtrados por source — su
// `income` refleja el origen seleccionado, así la proyección es coherente
// con el resto de la pestaña.
//
// ⚠️ ESTA TARJETA SE APAGA CUANDO LA PANTALLA YA TIENE "EL MES".
// La cuenta de acá es una regla de tres —lo que llevas más el promedio diario
// por los días que faltan— y da un monto EXACTO. El bloque `Stats2/ElMes.js`
// contesta la misma pregunta con un RANGO calibrado contra el error histórico
// del propio cliente, y además concluye si el mes termina arriba o abajo del
// anterior. Los dos números no coinciden nunca, y el badge de crecimiento de
// acá puede pintarse verde mientras "El mes" dice que va a terminar abajo: dos
// cierres de mes que se contradicen, a una pestaña de distancia. Cuando conviven
// manda el rango, que es el que sabe cuánto se equivoca. Ver `sinProyeccion`.
const MonthProjection = ({ dailyDocs, monthlyDocs, source = "all" }) => {
  const projection = useMemo(() => {
    const now = moment();
    const currentMonth = now.format("YYYY-MM");
    const daysElapsed = now.date();
    const daysInMonth = now.daysInMonth();

    // Lo facturado en el mes SÍ incluye el día en curso.
    const thisMonthDocs = dailyDocs.filter((d) => d.date.startsWith(currentMonth));
    const currentIncome = thisMonthDocs.reduce((a, d) => a + (d.income || 0), 0);

    // El promedio con el que se proyecta, NO: dividir por un día a medio
    // facturar proyectaría de menos toda la mañana. Y como hoy ya está contado
    // en `currentIncome`, los días que faltan son los posteriores a hoy.
    const diasCompletos = sinDiaEnCurso(thisMonthDocs);
    const incomeCompletos = diasCompletos.reduce((a, d) => a + (d.income || 0), 0);
    const dailyAvg = diasCompletos.length > 0
      ? incomeCompletos / diasCompletos.length
      : (thisMonthDocs.length > 0 ? currentIncome / thisMonthDocs.length : 0);
    const remainingDays = daysInMonth - daysElapsed;
    const projected = currentIncome + dailyAvg * remainingDays;

    // Last month for comparison
    const lastMonth = monthlyDocs.find((m) =>
      m.month === now.clone().subtract(1, "month").format("YYYY-MM")
    );

    const growth = lastMonth ? pct(projected, lastMonth.income) : null;

    return {
      currentIncome,
      projected,
      dailyAvg,
      daysElapsed,
      daysInMonth,
      lastMonthIncome: lastMonth?.income,
      growth,
      pctComplete: (daysElapsed / daysInMonth) * 100,
    };
  }, [dailyDocs, monthlyDocs]);

  const sourceTag = source === "tickets" ? " · Tickets"
    : source === "monthly" ? " · Abonados"
    : "";

  return (
    <Card className="shadow-sm mb-3" style={{ borderLeft: "3px solid var(--bs-primary)" }}>
      <CardHeader className="bg-white py-2">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1 text-primary" style={{ fontSize: 15, verticalAlign: "middle" }}>rocket_launch</span>
          Si sigues a este ritmo, así termina el mes
          <span className="text-primary ms-1" style={{ ...TITULO_ACLARACION, color: undefined, textTransform: "capitalize" }}>
            ({moment().format("MMMM YYYY")}{sourceTag})
          </span>
        </h6>
      </CardHeader>
      <CardBody>
        <Row className="g-3">
          <Col md={3} xs={6}>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>ACUMULADO</div>
            <h5 className="text-success mb-0">{fmt(projection.currentIncome)}</h5>
            <div className="text-muted" style={{ fontSize: 11 }}>
              {projection.daysElapsed} de {projection.daysInMonth} días ({projection.pctComplete.toFixed(0)}% del mes)
            </div>
          </Col>
          <Col md={3} xs={6}>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>PROYECTADO</div>
            <h5 className="text-primary mb-0">{fmt(projection.projected)}</h5>
            <div className="text-muted" style={{ fontSize: 11 }}>
              Estimado al ritmo actual, {fmt(projection.dailyAvg)} por día
            </div>
          </Col>
          <Col md={3} xs={6}>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>MES ANTERIOR</div>
            <h5 className="mb-0">{fmt(projection.lastMonthIncome)}</h5>
            <div className="text-muted" style={{ fontSize: 11 }}>el mes completo</div>
          </Col>
          <Col md={3} xs={6}>
            <div className="text-muted mb-1" style={{ fontSize: 11 }}>CRECIMIENTO ESTIMADO</div>
            <h5 className="mb-0">
              <GrowthBadge value={projection.growth} />
            </h5>
            <div className="text-muted" style={{ fontSize: 11 }}>vs mes anterior</div>
          </Col>
        </Row>
        <div className="progress mt-3" style={{ height: 8 }}>
          <div
            className="progress-bar bg-primary"
            style={{ width: `${projection.pctComplete}%` }}
          />
        </div>
      </CardBody>
    </Card>
  );
};

// ── Growth table with clickable rows ────────────────────────
// `source` ajusta qué columnas tienen sentido. En "monthly" se ocultan
// "Egresos" (no se atribuyen al origen), "Tickets" y "Ticket prom." (no
// aplican a abonados). En "tickets" se oculta sólo "Egresos".
const GrowthTable = ({ monthlyDocs, selectedMonth, onSelectMonth, source = "all" }) => {
  const sorted = useMemo(
    () => [...monthlyDocs].sort((a, b) => b.month.localeCompare(a.month)),
    [monthlyDocs]
  );

  if (sorted.length < 1) return null;

  const showExpenses = source === "all";
  const showTicketCols = source !== "monthly";

  const incomeLabel = source === "tickets" ? "Ingresos (Tickets)"
    : source === "monthly" ? "Ingresos (Abonados)"
    : "Ingresos";

  return (
    <Card className="shadow-sm mb-3">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>swap_vert</span>
          Cómo cambió cada mes respecto del anterior
          <span className="ms-2" style={TITULO_ACLARACION}>
            (elige un mes de la tabla para ver su detalle más abajo)
          </span>
        </h6>
        {/* Esta tabla arma sus <th> a mano, asi que las columnas del export se
            declaran aca. Si se agrega una columna arriba, se agrega aca. */}
        <BotonesExportar
          columnas={[
            { key: "month", label: "Mes", valor: (m) => moment(m.month, "YYYY-MM").format("MM/YYYY") },
            { key: "income", label: incomeLabel },
            ...(showExpenses ? [{ key: "expense", label: "Salidas de caja" }] : []),
            ...(showTicketCols ? [
              { key: "tickets", label: "Tickets", valor: (m) => m.tickets?.total?.out || 0 },
              { key: "avg", label: "Ticket promedio", valor: (m) => {
                const out = m.tickets?.total?.out || 0;
                return out > 0 ? Math.round(m.tickets.total.amount / out) : 0;
              } },
            ] : []),
            { key: "best", label: "Mejor día", valor: (m) => (m.best_day?.date
              ? moment(m.best_day.date).format("DD/MM/YYYY") : "") },
            { key: "bestAmt", label: "Ingreso del mejor día", valor: (m) => m.best_day?.income ?? "" },
          ]}
          filas={sorted}
          nombre="crecimiento-mes-a-mes"
        />
      </CardHeader>
      <CardBody>
        <Table bordered size="sm" responsive className="mb-0 align-middle">
          <thead>
            <tr className="table-light">
              <th>Mes</th>
              <th className="text-end">{incomeLabel}</th>
              {showExpenses && <th className="text-end">Salidas de caja</th>}
              <th className="text-end">Variación</th>
              {showTicketCols && <th className="text-end">Tickets cobrados</th>}
              {showTicketCols && <th className="text-end">Ticket promedio</th>}
              <th className="text-end">Mejor día</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m, i) => {
              const prev = sorted[i + 1];
              const growth = prev ? pct(m.income, prev.income) : null;
              const totalOut = m.tickets?.total?.out || 0;
              const avgTicket = totalOut > 0 ? m.tickets.total.amount / totalOut : 0;
              const isSelected = selectedMonth === m.month;

              return (
                <tr
                  key={m.month}
                  onClick={() => onSelectMonth(m.month)}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected ? "rgba(85, 110, 230, 0.08)" : undefined,
                  }}
                >
                  <td style={{ textTransform: "capitalize", fontWeight: 500 }}>
                    {isSelected && (
                      <span className="material-symbols-rounded me-1 text-primary" style={{ fontSize: 12 }}>
                        arrow_right
                      </span>
                    )}
                    {moment(m.month, "YYYY-MM").format("MMM YYYY")}
                  </td>
                  <td className="text-end fw-bold text-success">{fmt(m.income)}</td>
                  {showExpenses && <td className="text-end text-danger">{fmt(m.expense)}</td>}
                  <td className="text-end"><GrowthBadge value={growth} /></td>
                  {/* SALIDAS, no entradas. La columna de al lado divide el
                      importe por las salidas para sacar el ticket promedio:
                      con entradas aca la cuenta no cerraba, y las dos
                      columnas hablaban de cosas distintas estando pegadas. */}
                  {showTicketCols && <td className="text-end">{totalOut}</td>}
                  {showTicketCols && <td className="text-end text-info">{fmt(avgTicket)}</td>}
                  <td className="text-end">
                    {m.best_day?.date && (
                      <span style={{ fontSize: 12 }}>
                        {moment(m.best_day.date).format("DD/MM")} ({fmt(m.best_day.income)})
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
};

// ── Month detail cards (for selected month) ──────────────────
// `month` viene del array filtrado: `month.income` ya refleja la fuente.
// Los promedios (`daily_income`, `daily_tickets_in`, `avg_ticket`) están
// pre-calculados sobre el total — sólo los mostramos en source="all".
const MonthDetail = ({ monthlyDocs, selectedMonth, source = "all" }) => {
  const month = useMemo(
    () => monthlyDocs.find((m) => m.month === selectedMonth),
    [monthlyDocs, selectedMonth]
  );

  if (!month) return null;

  const worstIsZero = month.worst_day && (month.worst_day.income === 0 || !month.worst_day.income);
  const showAverages = source === "all";
  const showExpenses = source === "all";
  const sourceLabel = source === "tickets" ? " (Tickets)"
    : source === "monthly" ? " (Abonados)"
    : "";

  return (
    <Row className="g-3 mb-3">
      <Col lg={4} md={6}>
        <Card className="shadow-sm h-100">
          <CardBody className="py-3">
            <h6 className="mb-3" style={TITULO_CARD}>
              <span className="material-symbols-rounded me-1" style={{ fontSize: 15, verticalAlign: "middle" }}>analytics</span>
              {showAverages ? "El mes en promedio" : "El mes en resumen"}
              <span className="text-primary ms-1" style={{ fontSize: 11.5, fontWeight: 400, textTransform: "capitalize" }}>
                ({moment(month.month, "YYYY-MM").format("MMMM YYYY")})
              </span>
            </h6>
            {showAverages && (
              <>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted" style={{ fontSize: 13 }}>Ingreso diario</span>
                  <span className="fw-bold">{fmt(month.averages?.daily_income)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted" style={{ fontSize: 13 }}>Tickets por día</span>
                  <span className="fw-bold">{(month.averages?.daily_tickets_in || 0).toFixed(0)}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted" style={{ fontSize: 13 }}>Ticket promedio</span>
                  <span className="fw-bold">{fmt(month.averages?.avg_ticket)}</span>
                </div>
                <hr className="my-2" />
              </>
            )}
            <div className="d-flex justify-content-between mb-1">
              <span className="text-muted" style={{ fontSize: 13 }}>Ingresos{sourceLabel}</span>
              <span className="fw-bold text-success">{fmt(month.income)}</span>
            </div>
            {showExpenses && (
              <>
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted" style={{ fontSize: 13 }}>Salidas de caja</span>
                  <span className="fw-bold text-danger">{fmt(month.expense)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted" style={{ fontSize: 13 }}>Neto</span>
                  <span className={`fw-bold ${(month.net || month.income - month.expense) >= 0 ? "text-success" : "text-danger"}`}>
                    {fmt(month.net || (month.income || 0) - (month.expense || 0))}
                  </span>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>
      {month.best_day && (
        <Col lg={4} md={6}>
          <Card className="shadow-sm h-100" style={{ borderLeft: "3px solid var(--bs-success)" }}>
            <CardBody className="py-3">
              <h6 className="mb-3" style={TITULO_CARD}>
                <span className="material-symbols-rounded me-1 text-success" style={{ fontSize: 15, verticalAlign: "middle" }}>emoji_events</span>
                Tu mejor día del mes
              </h6>
              <h4 className="text-success mb-1">{fmt(month.best_day.income)}</h4>
              <span className="text-muted" style={{ fontSize: 13, textTransform: "capitalize" }}>
                {moment(month.best_day.date).format("dddd DD/MM/YYYY")}
              </span>
            </CardBody>
          </Card>
        </Col>
      )}
      {month.worst_day && (
        <Col lg={4} md={6}>
          <Card className="shadow-sm h-100" style={{ borderLeft: "3px solid var(--bs-danger)" }}>
            <CardBody className="py-3">
              <h6 className="mb-3" style={TITULO_CARD}>
                <span className="material-symbols-rounded me-1 text-danger" style={{ fontSize: 15, verticalAlign: "middle" }}>trending_down</span>
                Tu día más flojo del mes
              </h6>
              <h4 className="text-danger mb-1">{fmt(month.worst_day.income)}</h4>
              <span className="text-muted" style={{ fontSize: 13, textTransform: "capitalize" }}>
                {moment(month.worst_day.date).format("dddd DD/MM/YYYY")}
              </span>
              {worstIsZero && (
                <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                  <span className="material-symbols-rounded me-1" style={{ fontSize: 12 }}>info</span>
                  Es posible que ese día no hayas abierto
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      )}
    </Row>
  );
};

// ── Tab principal ────────────────────────────────────────────
// `sinProyeccion` es OPCIONAL y viene de afuera, igual que el `periodo` de
// TabIngresos y TabTickets: si la pantalla que monta esta pestaña ya trae su
// propio cierre de mes —`Stats2/ElMes.js`—, la tarjeta de proyección de acá se
// apaga. Dos proyecciones del mismo mes con dos cuentas distintas en la misma
// pantalla no son más información: son una pantalla que se contradice sola.
const TabMensual = ({ monthlyDocs, dailyDocs, sinProyeccion = false }) => {
  // CategorySummary se auto-oculta con menos de 2 categorias: replicamos esa
  // condicion para no dejar un contenedor de grafico vacio.
  const monthlyCategories = useCategories(monthlyDocs || []);
  const hasMonthlyCategories = (monthlyCategories || []).length >= 2;
  const [source, setSource] = useState("all");

  // Aplicamos el filtro UNA VEZ acá y bajamos los docs ya filtrados a
  // todos los componentes hijos. Cada uno usa `d.income` directamente
  // sin recalcular según el origen.
  const filteredMonthly = useMemo(
    () => applySourceFilter(monthlyDocs, source),
    [monthlyDocs, source]
  );
  const filteredDaily = useMemo(
    () => applySourceFilter(dailyDocs, source),
    [dailyDocs, source]
  );

  const sorted = useMemo(
    () => [...filteredMonthly].sort((a, b) => b.month.localeCompare(a.month)),
    [filteredMonthly]
  );

  const [selectedMonth, setSelectedMonth] = useState(null);
  // Si cambia el source, reseteamos al mes más reciente (para evitar quedar
  // en un mes que ya no tiene datos relevantes).
  React.useEffect(() => {
    if (sorted.length > 0 && (!selectedMonth || !sorted.find((m) => m.month === selectedMonth))) {
      setSelectedMonth(sorted[0].month);
    }
  }, [sorted, selectedMonth]);

  // Tendencias mensuales usa datos pre-calculados (daily_income,
  // daily_tickets_in) que no separan por origen — sólo lo mostramos cuando
  // source="all" para no engañar al lector.
  const showTrends = source === "all";
  // Composición es redundante con el bar chart cuando hay 1 sola serie
  // (caso "monthly"). En "tickets" sigue siendo útil porque muestra
  // hora/estadía/turno/acuerdos.
  const showComposition = source !== "monthly";

  return (
    <>
      <Card className="shadow-sm mb-3">
        <CardBody className="py-2 px-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="text-muted" style={{ fontSize: 12 }}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
              filter_list
            </span>
            Compara mes contra mes. Elige qué ingresos mirar:
          </div>
          <SourceSelector value={source} onChange={setSource} />
        </CardBody>
      </Card>

      {!sinProyeccion && (
        <MonthProjection dailyDocs={filteredDaily} monthlyDocs={filteredMonthly} source={source} />
      )}
      {sorted.length > 0 ? (
        <>
          <ExpandableChart title="Cuánto dejó cada mes">
            <MonthlyBarChart monthlyDocs={filteredMonthly} source={source} />
          </ExpandableChart>
          <Row className="g-3 mb-3">
            {showTrends && (
              <Col xl={showComposition ? 6 : 12}>
                <ExpandableChart title="Cómo viene tu día promedio, mes a mes">
                  <MonthlyTrendsChart monthlyDocs={filteredMonthly} />
                </ExpandableChart>
              </Col>
            )}
            {showComposition && (
              <Col xl={showTrends ? 6 : 12}>
                <ExpandableChart title="De dónde salió la plata de cada mes">
                  <RevenueCompositionChart monthlyDocs={filteredMonthly} source={source} />
                </ExpandableChart>
              </Col>
            )}
          </Row>
          <GrowthTable
            monthlyDocs={filteredMonthly}
            selectedMonth={selectedMonth}
            onSelectMonth={setSelectedMonth}
            source={source}
          />
          <MonthDetail monthlyDocs={filteredMonthly} selectedMonth={selectedMonth} source={source} />

          {/* El corte por categoria y por medio de pago solo existia sobre los
              documentos DIARIOS, que se cargan hasta 90 dias: no habia forma de
              comparar enero contra enero. Los documentos mensuales traen las dos
              cosas (`categories` y `payments`) desde siempre y nadie las leia. */}
          <Row className="g-3 mb-3">
            {hasMonthlyCategories && (
              <Col xl={6}>
                <ExpandableChart title="Categorías, mes a mes">
                  <CategorySummary docs={filteredMonthly} />
                </ExpandableChart>
              </Col>
            )}
            <Col xl={hasMonthlyCategories ? 6 : 12}>
              <ExpandableChart title="Cómo te pagaron, mes a mes">
                <PaymentBreakdown
                  livePayments={null}
                  dailyDocs={filteredMonthly}
                  period={filteredMonthly.length}
                  periodLabel={`últimos ${filteredMonthly.length} meses`}
                />
              </ExpandableChart>
            </Col>
          </Row>
        </>
      ) : (
        <Card className="shadow-sm">
          <CardBody className="text-center py-4">
            <span className="material-symbols-rounded text-muted d-block mb-2" style={{ fontSize: 36 }}>
              hourglass_empty
            </span>
            <h6 style={TITULO_CARD}>Aún no hay ningún mes cerrado</h6>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              El resumen de un mes se arma solo cuando el mes termina.
              {/* Sin la tarjeta de proyección arriba, mandar a mirarla es
                  mandar a mirar algo que no está en la pantalla. */}
              {!sinProyeccion && " Mientras tanto, arriba tienes la proyección del mes en curso."}
            </p>
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default TabMensual;
